import { getFormattingLocale } from '@/composables/useLocale';
import { i18n } from '@/i18n/translations';
import { getBaseLanguage } from 'common/utils/locale.utils';
import { now, nowMs } from 'common/utils/date.utils';
import { Locale, addDays, differenceInCalendarDays, format as formatDateFns, isSameDay, subDays } from 'date-fns';
import { de, deAT, enGB } from 'date-fns/locale';

// `t` stays reactive when destructured; the locale must be read per call (it changes at runtime)
const { t } = i18n.global;

// Statically imported (tree-shaking): one entry per base language, plus regional tags needing distinct formatting.
const DATE_FNS_LOCALES: Partial<Record<string, Locale>> = {
  de,
  en: enGB,
  'de-AT': deAT,
};

const getCurrentLanguage = (): string => getBaseLanguage(getFormattingLocale());

export const getDateFnsLocale = (): Locale => {
  const formattingLocale = getFormattingLocale();
  return DATE_FNS_LOCALES[formattingLocale] ?? DATE_FNS_LOCALES[getBaseLanguage(formattingLocale)] ?? de;
};

export const isToday = (val: string | Date): boolean => isSameDay(typeof val === 'string' ? new Date(val) : val, now());

export const isYesterday = (val: string | Date): boolean =>
  isSameDay(typeof val === 'string' ? new Date(val) : val, subDays(now(), 1));

export const isTomorrow = (val: string | Date): boolean =>
  isSameDay(typeof val === 'string' ? new Date(val) : val, addDays(now(), 1));

export const dateToMMddyyyy = (val: string | Date): string => {
  switch (getCurrentLanguage()) {
    case 'en':
      return formatDateFns(val, 'MM.dd.yyyy', { locale: getDateFnsLocale() });
    default:
      return formatDateFns(val, 'dd.MM.yyyy', { locale: getDateFnsLocale() });
  }
};

export const dateToddMMMMyyyyHmm = (val: string | Date): string => {
  return formatDateFns(val, 'dd. MMMM yyyy, H:mm', { locale: getDateFnsLocale() });
};

export const dateToddMMMMyyyyHmmss = (val: string | Date): string => {
  return formatDateFns(val, 'dd. MMMM yyyy, H:mm:ss', { locale: getDateFnsLocale() });
};

export const dateTodMMMMyyyy = (val: string | Date): string => {
  return formatDateFns(val, 'd. MMMM yyyy', { locale: getDateFnsLocale() });
};

export const dateTodMMMM = (val: string | Date): string => {
  return formatDateFns(val, 'd. MMMM', { locale: getDateFnsLocale() });
};

export const dateTodMMMyyyy = (val: string | Date): string => {
  return formatDateFns(val, 'd. MMM yyyy', { locale: getDateFnsLocale() });
};

export const dateTodMMMyyyyCompact = (val: string | Date): string => {
  return formatDateFns(val, 'd.MMM yyyy', { locale: getDateFnsLocale() });
};

export const getDatePrefix = (val: string | Date): string => {
  const date = typeof val === 'string' ? new Date(val) : val;
  let prefix = '';

  if (isToday(date)) {
    prefix = t('date.today');
  } else if (isYesterday(date)) {
    prefix = t('date.yesterday');
  } else if (isTomorrow(date)) {
    prefix = t('date.tomorrow');
  } else {
    prefix = formatDateFns(date, 'EEEE', { locale: getDateFnsLocale() });
  }

  return `${prefix},`;
};

export const getDateForObservationTimeline = (val: string | Date): string => {
  const date = typeof val === 'string' ? new Date(val) : val;
  let prefix = '';

  if (isToday(date)) {
    prefix = t('date.today');
  } else if (isYesterday(date)) {
    prefix = t('date.yesterday');
  } else if (isTomorrow(date)) {
    prefix = t('date.tomorrow');
  } else {
    prefix = dateTodMMMMyyyy(date);
  }

  return `${prefix}`;
};

export const getDatePrefixInlineObservation = (val: string | Date): string => {
  const date = typeof val === 'string' ? new Date(val) : val;
  const formattedDate = dateTodMMMMyyyy(date);

  if (isToday(date)) {
    return `${t('date.today')}, ${formattedDate}`;
  }
  if (isYesterday(date)) {
    return `${t('date.yesterday')}, ${formattedDate}`;
  }
  if (isTomorrow(date)) {
    return `${t('date.tomorrow')}, ${formattedDate}`;
  }

  return formattedDate;
};

export const dateToHHmm = (val: string | Date): string => {
  return formatDateFns(val, 'HH:mm', { locale: getDateFnsLocale() });
};

export const getDateLong = (val: string | Date): string => {
  return `${getDatePrefix(val)} ${dateTodMMMMyyyy(val)}`;
};

export const getYearOfDateYY = (val: string | Date): string => {
  return new Date(val).getFullYear().toString().slice(-2);
};

export const getYearOfDateYYYY = (val: string | Date): string => {
  return new Date(val).getFullYear().toString();
};

export const daysUntil = (dateIso?: string | null): number | null => {
  if (!dateIso) return null;
  const end = new Date(dateIso).getTime();
  const currentTimeMs = nowMs();
  return differenceInCalendarDays(end, currentTimeMs);
};
